import { useEffect, useMemo, useRef } from 'react'
import Chart from 'react-apexcharts'
import type { ChartCard, Timeframe } from '@/features/dashboard/api/schemas'
import {
  barWidthPx,
  expandedBarModel,
  expandedBarOptions,
  expandedBarSeries,
} from './lib/expandedBarOptions'
import { hideHover } from './lib/apexDom'
import { chartSummary } from './lib/chartSummary'
import { useChartPalette } from './lib/useChartPalette'
import { useElementSize } from './lib/useElementSize'
import styles from './ExpandedBarChart.module.css'

type ExpandedBarChartProps = {
  card: ChartCard
  /** The timeframe the card is drawn in: the YTD view shows its outlook as pale bars. */
  timeframe?: Timeframe | undefined
  /** The bar the panel speaks of; the latest reported one when unset. */
  selectedIndex?: number | undefined
  /** Called with the index of the bar clicked. */
  onSelect?: ((index: number) => void) | undefined
}

/** The expanded card's column chart: neutral sand bars over dashed gridlines. */
export const ExpandedBarChart = ({
  card,
  timeframe,
  selectedIndex,
  onSelect,
}: ExpandedBarChartProps) => {
  const slotRef = useRef<HTMLDivElement>(null)
  const { width, height } = useElementSize(slotRef)
  const { theme, palette } = useChartPalette()

  const model = useMemo(
    () => expandedBarModel(card, timeframe, selectedIndex),
    [card, timeframe, selectedIndex]
  )
  // Options depend on the bar's px width, not the slot's, so a resize that
  // leaves it unchanged doesn't rebuild them.
  const barPx = barWidthPx(width, model.values.length)
  const options = useMemo(
    () => expandedBarOptions(card, model, palette, barPx, onSelect),
    [card, model, palette, barPx, onSelect]
  )
  const series = useMemo(() => expandedBarSeries(card, model), [card, model])

  // The pointer may leave with the browser, which Apex never hears of.
  useEffect(() => {
    const hide = () => hideHover(slotRef.current)
    const onVisibility = () => {
      if (document.hidden) hide()
    }
    window.addEventListener('blur', hide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', hide)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return (
    <div
      ref={slotRef}
      className={styles.slot}
      role='img'
      aria-label={chartSummary(card)}
    >
      {width > 0 && height > 0 && (
        <Chart
          // A new theme redraws the chart, so its bars are reshaped.
          key={theme}
          type='bar'
          width={width}
          height={height}
          options={options}
          series={series}
        />
      )}
    </div>
  )
}
